import React, { useState, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { usePopper } from "react-popper";
import { bottom, top, left, right } from "@popperjs/core";
import { classNames } from "../../helpers/classNames";
import Button from "./Button";

const Dropdown = ({
  data = [],
  label,
  labelClasses,
  buttonClasses,
  buttonLabelClasses,
  buttonIcon,
  buttonIconPosition,
  buttonLabel,
  dropdownContainer,
  headerdropdownlabel,
  headerlabelicon,
  headerlabel,
  xPlacement = "bottomLeft",
  flip = true,
  buttonArrowIconClasses,
  buttonArrowIcon = "fa-regular fa-chevron-down",
  button,
  onChange = () => {},
  dropdownHeader = false,
  manage = false,
  manageLabel,
  manageLinkLabel,
  manageLink,
  manageIcon,
  dropdownHeaderLink,
  dropdownHeaderLabel,
}) => {
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement || "bottomLeft"],
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 0],
        },
      },
      !flip && {
        name: "flip",
        options: {
          flipVariations: false,
          fallbackPlacements: ["bottom-end"],
        },
      },
    ],
  });

  return (
    <Menu as="div" className="relative text-left">
      {() => (
        <>
          <div className="relative space-y-1 w-full">
            {label && (
              <div
                className={classNames(
                  "text-sm font-semibold text-opplio-gray tracking-none",
                  labelClasses
                )}
              >
                {label}
              </div>
            )}
            <div className="relative w-full">
              <MenuButton
                ref={setTargetElement}
                className={classNames(
                  "flex items-center justify-center space-x-2 h-10 border border-opplio-border px-3 pr-8 rounded-md transition-all duration-200",
                  buttonClasses
                )}
              >
                {button ? (
                  button
                ) : (
                  <>
                    {buttonIconPosition === "left" && (
                      <i className={classNames("fa-fw", buttonIcon)}></i>
                    )}
                    {buttonLabel && (
                      <div
                        className={classNames(
                          "text-base  block !leading-none",
                          buttonLabelClasses
                        )}
                      >
                        {buttonLabel}
                      </div>
                    )}
                    {buttonIconPosition === "right" && (
                      <i className={classNames("fa-fw", buttonIcon)}></i>
                    )}
                    {buttonArrowIcon && (
                      <div
                        className={classNames(
                          "absolute top-1/2 right-1 -translate-y-1/2",
                          buttonArrowIconClasses
                        )}
                      >
                        <i className={classNames("fa-fw", buttonArrowIcon)}></i>
                      </div>
                    )}
                  </>
                )}
              </MenuButton>
            </div>
          </div>

          <Transition
            as={Fragment}
            className="z-50"
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
            ref={popperElRef}
            style={styles.popper}
            {...attributes.popper}
          >
            <MenuItems
              modal={false}
              className={classNames(
                "absolute mt-1 w-56 origin-top-right rounded-md bg-white shadow-lg focus:outline-none",
                dropdownContainer
              )}
            >
              {dropdownHeader && (
                <div className="w-full px-4 py-3 border-b border-gray-100 bg-slate-100 rounded-t-md mb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col">
                      <div className="text-base font-semibold text-deep-purple">
                        {dropdownHeaderLabel}
                      </div>
                      {dropdownHeaderLink && (
                        <a
                          href={dropdownHeaderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-deep-purple hover:underline mt-0.5 truncate max-w-[180px]"
                        >
                          {dropdownHeaderLink}
                        </a>
                      )}
                    </div>
                    {dropdownHeaderLink && (
                      <a
                        href={dropdownHeaderLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-deep-purple transition"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square text-sm"></i>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {data.length > 0 &&
                data.map((item, i) => {
                  if (item?.type === "divider") {
                    return (
                      <div key={i}>
                        <div className="my-2 border-t border-gray-200" />
                        {manage && (
                          <div className="group flex w-full items-center justify-between px-3 py-1.5 gap-2 text-deep-purple font-medium text-sm ">
                            <div className="">{manageLabel}</div>
                            <Link
                              to={manageLink}
                              className="text-xs text-deep-purple flex gap-1.5"
                            >
                              {manageIcon && (
                                <i className={`${manageIcon} text-xs`}></i>
                              )}
                              {manageLinkLabel}
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <MenuItem key={item?._id || i}>
                      {({ active }) =>
                        item?.link ? (
                          <Link
                            to={item?.link}
                            className={classNames(
                              "group flex w-full items-center px-3 py-1.5 text-sm gap-2 transition-all duration-200 font-medium",
                              active
                                ? "hover:bg-primary/10 text-deep-purple"
                                : "text-deep-purple hover:bg-primary/10",
                              item?.class
                            )}
                          >
                            {item?.icon && (
                              <i
                                className={classNames("fa-fw", item?.icon)}
                              ></i>
                            )}
                            <span className="inline-block flex-1">
                              {item?.name}
                            </span>
                            {item?.extraRightContent && (
                              <span className="ml-auto">
                                {item.extraRightContent}
                              </span>
                            )}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className={classNames(
                              "group flex w-full items-center text-deep-purple px-3 py-1.5 text-sm gap-2 transition-all duration-200 font-medium",
                              active
                                ? item?.standout
                                  ? "bg-primary/10 text-deep-purple"
                                  : " text-deep-purple hover:bg-primary/10"
                                : "",
                              item?.class,
                              headerdropdownlabel
                            )}
                            onClick={() =>
                              typeof item?.click === "function"
                                ? item?.click(item)
                                : onChange(item)
                            }
                          >
                            {item?.icon && (
                              <span
                                className={classNames(
                                  "flex items-center justify-center",
                                  headerlabelicon
                                )}
                              >
                                <i
                                  className={classNames("fa-fw", item?.icon)}
                                ></i>
                              </span>
                            )}
                            <span
                              className={classNames("text-sm", headerlabel)}
                            >
                              {item?.name}
                            </span>
                            {item?.extraRightContent && (
                              <span className="ml-auto">
                                {item.extraRightContent}
                              </span>
                            )}
                            <span className="flex justify-end ml-auto">
                              <i className="fa-regular fa-angle-right"></i>
                            </span>
                          </button>
                        )
                      }
                    </MenuItem>
                  );
                })}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default Dropdown;
